import React, { useEffect, useState } from 'react';
import axios from 'axios';
import noDataImage from "../assets/nodata.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faSearch, faTimes, faCalendarAlt, faChevronDown, faChevronUp, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import SuperAdminSideNavbar from './helpers/superadminSideNavbar';
import ReactPaginate from 'react-paginate';
import Select from 'react-select';

const ErrorLogs = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isFilterExpanded, setIsFilterExpanded] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const logsPerPage = 10;

  // Filter options
  const [userOptions, setUserOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [platformOptions, setPlatformOptions] = useState([]);

  // Filter states (multi-select)
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Count active filters
  const activeFilterCount = [
    selectedUsers.length > 0,
    selectedSources.length > 0,
    selectedPlatforms.length > 0,
    startDate !== '',
    endDate !== ''
  ].filter(Boolean).length;

  // Custom styles for react-select - Enhanced
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '10px',
      border: state.isFocused ? '2px solid #2e3192' : '1px solid #e2e8f0',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(46, 49, 146, 0.1)' : '0 1px 3px rgba(0,0,0,0.04)',
      '&:hover': {
        border: '1px solid #a5b4fc'
      },
      minHeight: '46px',
      backgroundColor: '#fff',
      transition: 'all 0.2s ease'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#eef2ff',
      borderRadius: '6px',
      padding: '2px 4px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#2e3192',
      fontWeight: '500',
      fontSize: '13px'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#6366f1',
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: '#2e3192',
        color: '#fff'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#2e3192' : state.isFocused ? '#f0f1ff' : '#fff',
      color: state.isSelected ? '#fff' : '#374151',
      padding: '12px 16px',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#2e3192'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
      fontSize: '14px'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }),
    menuList: (base) => ({
      ...base,
      padding: '8px'
    }),
    input: (base) => ({
      ...base,
      color: '#374151'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#9ca3af',
      '&:hover': {
        color: '#2e3192'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#9ca3af',
      '&:hover': {
        color: '#ef4444'
      }
    })
  };

  // Fetch users for dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getallusersandadmins`);
        const users = response.data.filter((x) => x.RoleID !== 3);
        setUserOptions(users.map(user => ({
          value: user.UserID,
          label: user.UserName,
          email: user.Email
        })));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Fetch filter options (sources and platforms)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/geterrorlogfilters`);
        setSourceOptions((response.data.sources || []).map(source => ({
          value: source,
          label: source
        })));
        setPlatformOptions((response.data.platforms || []).map(platform => ({
          value: platform,
          label: platform
        })));
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch error logs with filters
  const fetchErrorLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (selectedUsers.length > 0) {
        params.append('userIds', selectedUsers.map(u => u.value).join(','));
      }
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedSources.length > 0) {
        params.append('sources', selectedSources.map(s => s.value).join(','));
      }
      if (selectedPlatforms.length > 0) {
        params.append('platforms', selectedPlatforms.map(p => p.value).join(','));
      }

      const queryString = params.toString();
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/geterrorlogsbyUserID${queryString ? `?${queryString}` : ''}`;
      
      const response = await axios.get(url);
      setLogs(response.data);
      setCurrentPage(0);
    } catch (err) {
      console.error('Failed to fetch error logs:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchErrorLogs();
  }, []);

  const handleApplyFilters = () => {
    fetchErrorLogs();
  };

  const handleResetFilters = async () => {
    setSelectedUsers([]);
    setSelectedSources([]);
    setSelectedPlatforms([]);
    setStartDate('');
    setEndDate('');
    setCurrentPage(0);
    
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/geterrorlogsbyUserID`);
      setLogs(response.data);
    } catch (err) {
      console.error('Failed to fetch error logs:', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Quick date presets
  const setDatePreset = (preset) => {
    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Get today's date in user's timezone
    const getTodayInTimezone = () => {
      const now = new Date();
      // Format date in user's timezone (YYYY-MM-DD)
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(now);
    };
    
    // Helper to get date N days ago in user's timezone
    const getDateDaysAgo = (days) => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    };
    
    // Helper to get date N months ago in user's timezone
    const getDateMonthsAgo = (months) => {
      const date = new Date();
      date.setMonth(date.getMonth() - months);
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date);
    };
    
    const today = getTodayInTimezone();
    
    switch(preset) {
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case 'week':
        setStartDate(getDateDaysAgo(7));
        setEndDate(today);
        break;
      case 'month':
        setStartDate(getDateMonthsAgo(1));
        setEndDate(today);
        break;
      default:
        break;
    }
  };

  const openDrawer = (log) => {
    setSelectedLog(log);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedLog(null);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getPlatformBadgeColor = (platform) => {
    const colors = {
      'LinkedIn': { bg: '#0077b5', color: '#fff' },
      'Twitter': { bg: '#1da1f2', color: '#fff' },
      'Facebook': { bg: '#4267b2', color: '#fff' },
      'Instagram': { bg: '#e4405f', color: '#fff' },
      'default': { bg: '#6b7280', color: '#fff' }
    };
    return colors[platform] || colors['default'];
  };

  // Custom option component for user select
  const UserOption = ({ data, ...props }) => (
    <div {...props.innerProps} style={{ 
      padding: '10px 16px', 
      cursor: 'pointer',
      backgroundColor: props.isFocused ? '#f0f1ff' : '#fff',
      borderBottom: '1px solid #f3f4f6'
    }}>
      <div style={{ fontWeight: '500', color: '#1f2937' }}>{data.label}</div>
      <div style={{ fontSize: '12px', color: '#6b7280' }}>{data.email}</div>
    </div>
  );

  // Pagination
  const pageCount = Math.ceil(logs.length / logsPerPage);
  const offset = currentPage * logsPerPage;
  const currentLogs = logs.slice(offset, offset + logsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  return (
    <SuperAdminSideNavbar>
      <div style={{ 
        backgroundColor: '#f8fafc', 
        minHeight: '100vh', 
        padding: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {/* Page Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#1e293b',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #2e3192 0%, #1e40af 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#fff', fontSize: '20px' }} />
              </span>
              Error Logs
            </h1>
            <p style={{ color: '#64748b', margin: '8px 0 0 54px', fontSize: '14px' }}>
              Monitor and track system errors across all platforms
            </p>
          </div>
          <button 
            onClick={fetchErrorLogs}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              borderRadius: '10px',
              border: 'none',
              background: 'linear-gradient(135deg, #2e3192 0%, #1e40af 100%)',
              color: '#fff',
              fontWeight: '600',
              fontSize: '14px',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 14px rgba(46, 49, 146, 0.3)',
              transition: 'all 0.2s ease',
              opacity: loading ? 0.7 : 1
            }}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            Refresh Data
          </button>
        </div>

        {/* Filter Panel */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.03)',
          marginBottom: '24px',
          overflow: 'hidden',
          border: '1px solid #e2e8f0'
        }}>
          {/* Filter Header */}
          <div 
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '18px 24px',
              cursor: 'pointer',
              borderBottom: isFilterExpanded ? '1px solid #e2e8f0' : 'none',
              background: '#fafbfc'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                backgroundColor: '#eef2ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FontAwesomeIcon icon={faSearch} style={{ color: '#2e3192', fontSize: '14px' }} />
              </div>
              <div>
                <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>
                  Search & Filter
                </span>
                {activeFilterCount > 0 && (
                  <span style={{
                    marginLeft: '10px',
                    backgroundColor: '#2e3192',
                    color: '#fff',
                    padding: '3px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {activeFilterCount} active filters
                  </span>
                )}
              </div>
            </div>
            <FontAwesomeIcon 
              icon={isFilterExpanded ? faChevronUp : faChevronDown} 
              style={{ color: '#64748b', transition: 'transform 0.2s ease' }} 
            />
          </div>

          {/* Filter Body */}
          {isFilterExpanded && (
            <div style={{ padding: '24px' }}>
              {/* Quick Date Presets */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#64748b', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '10px',
                  display: 'block'
                }}>
                  Quick Date Range
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Today', value: 'today' },
                    { label: 'Last 7 Days', value: 'week' },
                    { label: 'Last 30 Days', value: 'month' }
                  ].map(preset => (
                    <button
                      key={preset.value}
                      onClick={() => setDatePreset(preset.value)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        backgroundColor: '#fff',
                        color: '#475569',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.borderColor = '#2e3192';
                        e.target.style.color = '#2e3192';
                        e.target.style.backgroundColor = '#f0f1ff';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.color = '#475569';
                        e.target.style.backgroundColor = '#fff';
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filter Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
                gap: '20px',
                marginBottom: '24px'
              }}>
                {/* User Filter */}
                <div>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    👤 User
                  </label>
                  <Select
                    isMulti
                    options={userOptions}
                    value={selectedUsers}
                    onChange={setSelectedUsers}
                    placeholder="Select users..."
                    styles={selectStyles}
                    isClearable
                    isSearchable
                    closeMenuOnSelect={false}
                    components={{ Option: UserOption }}
                    noOptionsMessage={() => "No users found"}
                  />
                </div>

                {/* Date Range - From */}
                <div>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6366f1' }} />
                    From Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px',
                      color: '#374151',
                      backgroundColor: '#fff',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '2px solid #2e3192';
                      e.target.style.boxShadow = '0 0 0 3px rgba(46, 49, 146, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid #e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Date Range - To */}
                <div>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6366f1' }} />
                    To Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px',
                      color: '#374151',
                      backgroundColor: '#fff',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.border = '2px solid #2e3192';
                      e.target.style.boxShadow = '0 0 0 3px rgba(46, 49, 146, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.border = '1px solid #e2e8f0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                {/* Source Filter */}
                <div>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    📁 Source
                  </label>
                  <Select
                    isMulti
                    options={sourceOptions}
                    value={selectedSources}
                    onChange={setSelectedSources}
                    placeholder="Select sources..."
                    styles={selectStyles}
                    isClearable
                    isSearchable
                    closeMenuOnSelect={false}
                    noOptionsMessage={() => "No sources found"}
                  />
                </div>

                {/* Platform Filter */}
                <div>
                  <label style={{ 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#374151', 
                    marginBottom: '8px',
                    display: 'block'
                  }}>
                    🌐 Platform
                  </label>
                  <Select
                    isMulti
                    options={platformOptions}
                    value={selectedPlatforms}
                    onChange={setSelectedPlatforms}
                    placeholder="Select platforms..."
                    styles={selectStyles}
                    isClearable
                    isSearchable
                    closeMenuOnSelect={false}
                    noOptionsMessage={() => "No platforms found"}
                  />
                </div>
              </div>

              {/* Filter Actions */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #f1f5f9'
              }}>
                <button
                  onClick={handleResetFilters}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#fff',
                    color: '#64748b',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  Clear All
                </button>
                <button
                  onClick={handleApplyFilters}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 28px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #2e3192 0%, #1e40af 100%)',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(46, 49, 146, 0.25)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results Card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.03)',
          overflow: 'hidden',
          border: '1px solid #e2e8f0'
        }}>
          {/* Results Header */}
          <div style={{
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#fafbfc'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>
                Results
              </span>
              <span style={{
                backgroundColor: '#f1f5f9',
                color: '#475569',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {logs.length} total
              </span>
            </div>
            {!loading && logs.length > 0 && (
              <span style={{ color: '#64748b', fontSize: '13px' }}>
                Showing {offset + 1}-{Math.min(offset + logsPerPage, logs.length)} of {logs.length}
              </span>
            )}
          </div>

          {/* Table Content */}
          <div style={{ padding: loading || logs.length === 0 ? '40px' : '0' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div className='spinner'></div>
                <p style={{ color: '#64748b', marginTop: '16px', fontSize: '14px' }}>Loading error logs...</p>
              </div>
            ) : logs?.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <img src={noDataImage} alt="No Data" style={{ width: '200px', opacity: 0.8 }} />
                <p style={{ color: '#64748b', fontSize: '16px', marginTop: '16px', fontWeight: '500' }}>
                  No error logs found
                </p>
                <p style={{ color: '#94a3b8', fontSize: '14px' }}>
                  Try adjusting your filters or check back later
                </p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    backgroundColor: '#fff'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' }}>Content ID</th>
                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' }}>Post ID</th>
                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' }}>User</th>
                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' }}>Date & Time</th>
                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' }}>Source</th>
                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', whiteSpace: 'nowrap' }}>Platform</th>
                        <th style={{ padding: '14px 16px', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left', minWidth: '300px' }}>Error Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentLogs?.map((log, index) => {
                        const errorMessage = log.ErrorMessage;
                        const platformColors = getPlatformBadgeColor(log.Platform);
                        return (
                          <tr 
                            key={log.ErrorID}
                            style={{ 
                              backgroundColor: index % 2 === 0 ? '#fff' : '#f9fafb',
                              borderBottom: '1px solid #e5e7eb'
                            }}
                          >
                            <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                              {log.ContentID || '—'}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                              {log.PostID || '—'}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {log.UserID ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {log.UserName && (
                                    <span style={{ 
                                      color: '#1e293b',
                                      fontSize: '13px',
                                      fontWeight: '500'
                                    }}>
                                      {log.UserName}
                                    </span>
                                  )}
                                  {log.Email && (
                                    <span style={{ 
                                      color: '#64748b',
                                      fontSize: '11px'
                                    }}>
                                      {log.Email}
                                    </span>
                                  )}
                                  {!log.UserName && !log.Email && (
                                    <span style={{ 
                                      color: '#64748b',
                                      fontSize: '12px'
                                    }}>
                                      User ID: {log.UserID}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '12px 16px', color: '#475569', fontSize: '13px', whiteSpace: 'nowrap' }}>
                              {formatDateTime(log.ErrorDateTime)}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {log.Source ? (
                                <span style={{ 
                                  backgroundColor: '#e5e7eb',
                                  color: '#374151',
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  display: 'inline-block'
                                }}>
                                  {log.Source}
                                </span>
                              ) : '—'}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              {log.Platform ? (
                                <span style={{ 
                                  backgroundColor: platformColors.bg,
                                  color: platformColors.color,
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  display: 'inline-block'
                                }}>
                                  {log.Platform}
                                </span>
                              ) : '—'}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                              }}>
                                <span style={{ 
                                  color: '#991b1b',
                                  fontSize: '12px',
                                  backgroundColor: '#fef2f2',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  border: '1px solid #fecaca',
                                  maxWidth: '200px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {errorMessage ? errorMessage.substring(0, 25) + (errorMessage.length > 25 ? '...' : '') : '—'}
                                </span>
                                {errorMessage && (
                                  <button 
                                    onClick={() => openDrawer(log)}
                                    style={{ 
                                      color: '#fff',
                                      backgroundColor: '#2e3192',
                                      border: 'none',
                                      padding: '6px 12px',
                                      fontWeight: '600',
                                      fontSize: '11px',
                                      cursor: 'pointer',
                                      borderRadius: '4px',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    Read More
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pageCount > 1 && (
                  <div style={{ 
                    padding: '20px 24px', 
                    borderTop: '1px solid #e2e8f0',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <ReactPaginate
                      previousLabel={'← Previous'}
                      nextLabel={'Next →'}
                      breakLabel={'...'}
                      pageCount={pageCount}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={3}
                      onPageChange={handlePageClick}
                      containerClassName={'pagination'}
                      pageClassName={'page-item'}
                      pageLinkClassName={'page-link'}
                      previousClassName={'page-item'}
                      previousLinkClassName={'page-link'}
                      nextClassName={'page-item'}
                      nextLinkClassName={'page-link'}
                      breakClassName={'page-item'}
                      breakLinkClassName={'page-link'}
                      activeClassName={'active'}
                      forcePage={currentPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
              {/* Side Drawer */}
        {isDrawerOpen && (
          <>
            {/* Backdrop */}
            <div 
              onClick={closeDrawer}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
                transition: 'opacity 0.3s ease'
              }}
            />
            
            {/* Drawer */}
            <div style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: '450px',
              maxWidth: '90vw',
              height: '100vh',
              backgroundColor: '#fff',
              boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideIn 0.3s ease'
            }}>
              {/* Drawer Header */}
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f8fafc'
              }}>
                <div>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: '700', 
                    color: '#1e293b' 
                  }}>
                    Error Details
                  </h3>
                  <p style={{ 
                    margin: '4px 0 0', 
                    fontSize: '13px', 
                    color: '#64748b' 
                  }}>
                    {selectedLog?.Platform || 'System'} Error
                  </p>
                </div>
                <button 
                  onClick={closeDrawer}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              {/* Drawer Body */}
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '24px'
              }}>
                {/* Error Info Cards */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    backgroundColor: '#f8fafc',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Content ID</div>
                    <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>{selectedLog?.ContentID || '—'}</div>
                  </div>
                  <div style={{
                    backgroundColor: '#f8fafc',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Post ID</div>
                    <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>{selectedLog?.PostID || '—'}</div>
                  </div>
                  {selectedLog?.UserID && (
                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      gridColumn: 'span 2'
                    }}>
                      <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>User</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {selectedLog?.UserName && (
                          <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>{selectedLog.UserName}</div>
                        )}
                        {selectedLog?.Email && (
                          <div style={{ fontSize: '13px', color: '#64748b' }}>{selectedLog.Email}</div>
                        )}
                        {!selectedLog?.UserName && !selectedLog?.Email && (
                          <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>User ID: {selectedLog.UserID}</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div style={{
                    backgroundColor: '#f8fafc',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Source</div>
                    <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>{selectedLog?.Source || '—'}</div>
                  </div>
                  <div style={{
                    backgroundColor: '#f8fafc',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Platform</div>
                    <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>{selectedLog?.Platform || '—'}</div>
                  </div>
                </div>

                {/* Date/Time */}
                <div style={{
                  backgroundColor: '#f8fafc',
                  padding: '14px',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '24px'
                }}>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '600', marginBottom: '4px' }}>Error Date & Time</div>
                  <div style={{ fontSize: '15px', color: '#1e293b', fontWeight: '600' }}>{formatDateTime(selectedLog?.ErrorDateTime)}</div>
                </div>

                {/* Error Message */}
                <div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#64748b', 
                    textTransform: 'uppercase', 
                    fontWeight: '600', 
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#dc2626' }} />
                    Error Message
                  </div>
                  <div style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '10px',
                    padding: '16px',
                    color: '#991b1b',
                    fontSize: '13px',
                    lineHeight: '1.7',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    {selectedLog?.ErrorMessage || 'No error message available'}
                  </div>
                </div>
              </div>

              {/* Drawer Footer */}
              <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc'
              }}>
                <button 
                  onClick={closeDrawer}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#2e3192',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            {/* Animation styles */}
            <style>{`
              @keyframes slideIn {
                from {
                  transform: translateX(100%);
                }
                to {
                  transform: translateX(0);
                }
              }
            `}</style>
          </>
        )}
      </div>
    </SuperAdminSideNavbar>
  );
};

export default ErrorLogs;
